// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, Users, DollarSign, FileText, Package, TrendingUp, AlertTriangle, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const Dashboard = ({ setCurrentPage }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Reload dashboard data when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      setLoading(true);
      loadDashboardData();
      loadRecentInvoices();
      loadTopCustomers();
      loadLowStockProducts();
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      // Auto-select first company
      if (data.length > 0) {
        const savedCompanyId = localStorage.getItem('selectedCompanyId');
        if (savedCompanyId && data.find(c => c.id === parseInt(savedCompanyId))) {
          setSelectedCompanyId(savedCompanyId);
        } else {
          setSelectedCompanyId(data[0].id.toString());
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
  };

  const loadDashboardData = async () => {
    try {
      const data = selectedCompanyId
        ? await apiService.getDashboardDataByCompany(selectedCompanyId)
        : await apiService.getDashboardData();
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    }
  };

  const loadRecentInvoices = async () => {
    try {
      const data = selectedCompanyId
        ? await apiService.getRecentInvoicesByCompany(selectedCompanyId, 5)
        : await apiService.getRecentInvoices(5);
      setRecentInvoices(data);
    } catch (error) {
      console.error('Error loading recent invoices:', error);
    }
  };

  const loadTopCustomers = async () => {
    try {
      const data = await apiService.getTopCustomers();
      setTopCustomers(data.slice(0, 5)); // Top 5 customers
    } catch (error) {
      console.error('Error loading top customers:', error);
    }
  };

  const loadLowStockProducts = async () => {
    try {
      const data = selectedCompanyId
        ? await apiService.getLowStockProductsByCompany(selectedCompanyId, 10)
        : await apiService.getLowStockProducts(10);
      setLowStockProducts(data.slice(0, 5)); // Top 5 low stock
      setLoading(false);
    } catch (error) {
      console.error('Error loading low stock products:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };


  const getStatusColor = (status) => {
    const colors = {
      'PAID': 'text-green-600 bg-green-100',     
      'PARTIALLY_PAID': 'text-yellow-600 bg-yellow-100',
      'OVERDUE': 'text-red-600 bg-red-100',
      'UNPAID': 'text-grey-600 bg-red-100',
      
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getStockColor = (quantity) => {
    if (quantity === 0) return 'text-red-600';
    if (quantity <= 5) return 'text-yellow-600';
    if (quantity <= 2) return 'text-orange-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Default stats if dashboard data is not available
  const stats = dashboardData ? [
    {
      label: 'Total Revenue',
      value: formatCurrency(dashboardData.totalRevenue),
      change: `+${dashboardData.revenueGrowth || 0}%`,
      icon: DollarSign,
      color: 'green',
      positive: (dashboardData.revenueGrowth || 0) >= 0
    },
    {
      label: 'Overdue Invoices',
      value: dashboardData.overdueInvoices || 0,
      change: `+${dashboardData.customerGrowth || 0}%`,
      icon: Users,
      color: 'red',
      positive: (dashboardData.customerGrowth || 0) >= 0
    },
     {
      label: 'Total Customers',
      value: dashboardData.totalCustomers || 0,
      change: `+${dashboardData.customerGrowth || 0}%`,
      icon: Users,
      color: 'blue',
      positive: (dashboardData.customerGrowth || 0) >= 0
    },
    {
      label: 'Total Invoices',
      value: dashboardData.totalInvoices || 0,
      change: `+${dashboardData.invoiceGrowth || 0}%`,
      icon: FileText,
      color: 'purple',
      positive: (dashboardData.invoiceGrowth || 0) >= 0
    },
    {
      label: 'Products',
      value: dashboardData.totalProducts || 0,
      change: `${dashboardData.lowStockCount || 0} low stock`,
      icon: Package,
      color: 'orange',
      positive: (dashboardData.lowStockCount || 0) === 0
    }
  ] : [
    {
      label: 'Total Revenue',
      value: formatCurrency(0),
      change: '+0%',
      icon: DollarSign,
      color: 'green',
      positive: true
    },
    {
      label: 'Total Customers',
      value: '0',
      change: '+0%',
      icon: Users,
      color: 'blue',
      positive: true
    },
    {
      label: 'Total Invoices',
      value: '0',
      change: '+0%',
      icon: FileText,
      color: 'purple',
      positive: true
    },
    {
      label: 'Products',
      value: '0',
      change: '0 low stock',
      icon: Package,
      color: 'orange',
      positive: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600">Welcome back, {user?.name || 'User'}</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3 items-center">
          {/* Company Selector */}
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            <select
              value={selectedCompanyId}
              onChange={handleCompanyChange}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setCurrentPage('new-invoice')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Invoice</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
            <span className="text-sm text-yellow-700">{error}</span>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {user?.role === 'ADMIN' && stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
              </div>
            </div>
            <div className="mt-4">
              <span className={`text-sm ${stat.positive ? 'text-green-600' : 'text-red-600'}`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-500"> vs last period</span>
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Status Overview */}
      {dashboardData?.invoiceStatusBreakdown && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {Object.entries(dashboardData.invoiceStatusBreakdown).map(([status, count]) => (
              <div key={status} className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)} mb-2`}>
                  {status.replace('_', ' ')}
                </div>
                <div className="text-2xl font-bold text-gray-900">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Invoices</h3>
            <button
              onClick={() => setCurrentPage('invoices')}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentInvoices.length > 0 ? (
              recentInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</p>
                      <p className="text-xs text-gray-500">{invoice.customer?.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalAmount)}</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {invoice.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No recent invoices</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
         {user?.role === 'ADMIN' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setCurrentPage('new-invoice')}
              className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center"
            >
              <Plus className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">New Invoice</p>
            </button>
           
            <button
              onClick={() => setCurrentPage('customers')}
              className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center"
            >
              <Users className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">Add Customer</p>
            </button>
            <button
              onClick={() => setCurrentPage('products')}
              className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center"
            >
              <Package className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">Add Product</p>
            </button>
            <button
              onClick={() => setCurrentPage('reports')}
              className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-center"
            >
              <BarChart3 className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">View Reports</p>
            </button>           
          </div>
        </div>
        )}
      </div>
       

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Customers */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Customers</h3>
            <button
              onClick={() => setCurrentPage('customers')}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {topCustomers.length > 0 ? (
              topCustomers.map((customer, index) => (
                <div key={customer.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(customer.totalInvoiceAmount || 0)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {customer.invoiceCount || 0} invoices
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Users className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No customer data</p>
              </div>
            )}
          </div>
        </div>

        {/* Low Stock Products */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Low Stock Alert</h3>
            <button
              onClick={() => setCurrentPage('products')}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((product) => (
                <div key={product.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.sku}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${getStockColor(product.quantityInStock)}`}>
                      {product.quantityInStock} in stock
                    </p>
                    <p className="text-xs text-gray-500">{formatCurrency(product.price)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Package className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">All products in stock</p>
              </div>
            )}
          </div>
        </div>
      </div>

      

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">API Status: Online</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Database: Connected</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Last Updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;